export interface Workbook {
  number: string;
  title: string;
  description: string;
  tag: string;
  coverImage: string;
}

export const workbooks: Workbook[] = [
  {
    number: '01',
    title: 'The Matter & Change Workbook',
    description:
      'Build strong foundations. Students explore matter, its properties, states and physical vs chemical changes through visual summaries and guided completion activities.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/87WDowN752kkxPpK7GE4sC/volume_01_mockup_optimized.webp',
  },
  {
    number: '02',
    title: 'Atomic Structure & Trends Tracker',
    description:
      'From atomic models to periodic trends. Timeline activities, diagram labeling, isotope calculations and trend prediction tables — all scaffolded for student success.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/2zxudKHTWG9agHVJpvsMAz/volume_02_mockup_optimized.webp',
  },
  {
    number: '03',
    title: 'The Bonding & Lewis Script',
    description:
      'Electron configuration, ionic bonding, covalent bonding and VSEPR theory. Includes step-by-step Lewis structure drawing zones and orbital diagram activities.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/fJUgtWy9dWEh26XNaoz4bk/volume_03_mockup_optimized.webp',
  },
  {
    number: '04',
    title: 'Reaction & Stoichiometry Lab-on-Paper',
    description:
      'Nomenclature, equation balancing and full stoichiometry — scaffolded with calculation work spaces and lab simulation worksheets students can complete independently.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/uUYdjZM5P3U95XBaDqcokx/volume_04_mockup_optimized.webp',
  },
  {
    number: '05',
    title: 'Organic Chemistry & Bio-Systems Action Book',
    description:
      'Carbon chemistry, functional groups, polymers, biochemistry basics and environmental chemistry — taught through real-world case studies and application activities.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/eXZgNKSvrALAivoCFsr6kX/volume_05_mockup_optimized.webp',
  },
];

export interface Bonus {
  badge: string;
  icon: string;
  title: string;
  description: string;
  value: string;
}

export const bonuses: Bonus[] = [
  {
    badge: 'BONUS 1',
    icon: '📋',
    title: 'The Dual-Version Assessment Vault',
    description:
      'A complete bank of A/B assessments for every Chemistry topic. Same difficulty. Different data. Zero copying. Instant retakes. Version A for practice. Version B for the test. Zero prep time.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 2',
    icon: '🎨',
    title: 'Science Comics Engagement Pack',
    description:
      '10 original Chemistry comic stories with comprehension activities and science connection pages. For the students who think they hate Chemistry — until they read these.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 3',
    icon: '🔬',
    title: 'The Visual Lab Station Playbook',
    description:
      '7 complete lab protocols with step-by-step procedures, pre-formatted data sheets, safety checklists and a universal lab report template. Walk in. Print. Run the lab.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 4',
    icon: '📚',
    title: 'The Visual Chemistry Cheat-Sheet Library',
    description:
      '150+ visual reference cards covering every major Chemistry concept. Project them. Print them. Hand them out. Perfect for review, scaffolding and exam preparation.',
    value: '$47 USD',
  },
];

export interface Testimonial {
  text: string;
  name: string;
  city: string;
  role: string;
  initials: string;
}

export const testimonials: Testimonial[] = [
  {
    text: 'I used to spend every Sunday rebuilding the same stoichiometry lesson. With this system I printed Workbook 4 on Friday and walked in Monday completely ready. My students stayed engaged the entire class. That never happens.',
    name: 'Sarah M.',
    city: 'Houston, Texas',
    role: 'High School Chemistry Teacher',
    initials: 'SM',
  },
  {
    text: 'The Lab Station Playbook fixed the part of my week I dreaded most. Safety checklist, procedure, data sheet — all printed in one go. My labs finally run on time.',
    name: 'David O.',
    city: 'Manchester, United Kingdom',
    role: 'Head of Science, Secondary School',
    initials: 'DO',
  },
  {
    text: 'The A/B assessment vault alone paid for the whole thing. Same difficulty, different data. Retakes stopped being a Sunday project.',
    name: 'Amelia K.',
    city: 'Toronto, Ontario',
    role: 'Grade 11 & 12 Chemistry Teacher',
    initials: 'AK',
  },
  {
    text: 'I teach four sections of Chemistry. Having every worksheet, lab guide and review sheet in one printable system cut my prep from hours to minutes.',
    name: 'Marcus T.',
    city: 'Phoenix, Arizona',
    role: 'AP Chemistry Teacher',
    initials: 'MT',
  },
  {
    text: 'The Lewis structure drawing zones were the turning point. Students who had given up on bonding started finishing the page on their own.',
    name: 'Priya N.',
    city: 'Birmingham, United Kingdom',
    role: 'Chemistry Teacher, Years 10–13',
    initials: 'PN',
  },
  {
    text: 'I printed the Science Comics on a whim before a Friday period. Half the class asked if there were more. In Chemistry. On a Friday.',
    name: 'Jenna R.',
    city: 'Columbus, Ohio',
    role: 'High School Science Teacher',
    initials: 'JR',
  },
  {
    text: 'Everything is NGSS aligned and it shows. I stopped patching worksheets together from five different sites and just teach from one system.',
    name: 'Luis A.',
    city: 'Vancouver, British Columbia',
    role: 'Chemistry & Physics Teacher',
    initials: 'LA',
  },
];

export interface Notification {
  name: string;
  location: string;
  time: string;
}

export const notifications: Notification[] = [
  { name: 'Sarah M.', location: 'Houston, Texas', time: '2 minutes ago' },
  { name: 'David O.', location: 'Manchester, United Kingdom', time: '5 minutes ago' },
  { name: 'Amelia K.', location: 'Toronto, Ontario', time: '8 minutes ago' },
  { name: 'Marcus T.', location: 'Phoenix, Arizona', time: '11 minutes ago' },
  { name: 'Priya N.', location: 'Birmingham, United Kingdom', time: '14 minutes ago' },
  { name: 'Jenna R.', location: 'Columbus, Ohio', time: '18 minutes ago' },
  { name: 'Luis A.', location: 'Vancouver, British Columbia', time: '22 minutes ago' },
  { name: 'Rachel B.', location: 'Denver, Colorado', time: '26 minutes ago' },
  { name: 'Tom H.', location: 'Leeds, United Kingdom', time: '31 minutes ago' },
  { name: 'Nicole F.', location: 'Calgary, Alberta', time: '35 minutes ago' },
  { name: 'Eric S.', location: 'Portland, Oregon', time: '41 minutes ago' },
  { name: 'Hannah W.', location: 'Glasgow, Scotland', time: '47 minutes ago' },
  { name: 'Andre P.', location: 'Atlanta, Georgia', time: '52 minutes ago' },
  { name: 'Megan L.', location: 'Ottawa, Ontario', time: '58 minutes ago' },
  { name: 'Chris D.', location: 'Bristol, United Kingdom', time: '1 hour ago' },
  { name: 'Olivia G.', location: 'Minneapolis, Minnesota', time: '1 hr 8 min ago' },
  { name: 'Ben C.', location: 'Cardiff, Wales', time: '1 hr 15 min ago' },
  { name: 'Tanya V.', location: 'Sacramento, California', time: '1 hr 24 min ago' },
  { name: 'Peter M.', location: 'Halifax, Nova Scotia', time: '1 hr 33 min ago' },
  { name: 'Grace I.', location: 'Nashville, Tennessee', time: '1 hr 45 min ago' },
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: 'How do I receive the files after purchase?',
    answer:
      'The moment your payment is confirmed, you receive instant access to all 9 PDF files. No waiting. No apps. Download from any device immediately.',
  },
  {
    question: 'Is this a subscription?',
    answer: 'No. This is a one-time payment of $27 USD. You own the system forever.',
  },
  {
    question: 'Can I buy this outside the USA?',
    answer:
      'Yes. The system is available worldwide. All major cards are accepted from any country.',
  },
  {
    question: "What if it doesn't work for me?",
    answer:
      "We offer a 30-day full money-back guarantee. If you're not satisfied, contact us for a complete refund. No questions asked.",
  },
  {
    question: 'Is this a video course or certification?',
    answer:
      'No. This is a complete printable PDF system. No videos. No live sessions. No certification. Just ready-to-use classroom materials you can print and use immediately.',
  },
  {
    question: 'Do I need to install anything?',
    answer:
      'Nothing. The files are standard PDFs that open on any device — phone, tablet, laptop or desktop.',
  },
];
